import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AutoReport.css';

const AutoReport = ({ user }) => {
  const navigate = useNavigate();
  // State for current step
  const [currentStep, setCurrentStep] = useState(1);
  
  // Step 1: Student Input
  const [studentNamesInput, setStudentNamesInput] = useState('');
  const [studentNames, setStudentNames] = useState([]);
  const [nameError, setNameError] = useState('');
  
  // Step 2: Shared Class Details
  const [classInfo, setClassInfo] = useState({
    className: '',
    subject: '',
    term: '',
    unitsCovered: ''
  });
  
  // Step 3: Individual Student Details
  const [studentDetails, setStudentDetails] = useState([]);
  
  // Step 4: Generated Reports
  const [reports, setReports] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Update student details when student names change
  useEffect(() => {
    if (studentNames.length > 0) {
      const initialDetails = studentNames.map(name => ({
        name,
        level: 5,
        positives: '',
        areasToImprove: ''
      }));
      setStudentDetails(initialDetails);
    }
  }, [studentNames]);
  
  // Handle student names input
  const handleStudentNamesChange = (e) => {
    setStudentNamesInput(e.target.value);
  };
  
  // Process student names
  const processStudentNames = () => {
    const names = studentNamesInput
      .split(',')
      .map(name => name.trim())
      .filter(name => name !== '');
    
    if (names.length === 0) {
      setNameError('Please enter at least one student name.');
      return;
    }
    
    if (names.length > 30) {
      setNameError('You can only enter up to 30 students at a time.');
      return;
    }
    
    setNameError('');
    setStudentNames(names);
    setCurrentStep(2);
  };
  
  // Update individual student name
  const updateStudentName = (index, newName) => {
    const updatedNames = [...studentNames];
    updatedNames[index] = newName;
    setStudentNames(updatedNames);
    
    const updatedDetails = [...studentDetails];
    updatedDetails[index].name = newName;
    setStudentDetails(updatedDetails);
  };
  
  // Handle shared class details change
  const handleClassInfoChange = (e) => {
    const { name, value } = e.target;
    setClassInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Move to student details step
  const proceedToStudentDetails = () => {
    setCurrentStep(3);
  };
  
  // Handle individual student detail change
  const handleStudentDetailChange = (index, field, value) => {
    const updatedDetails = [...studentDetails];
    updatedDetails[index][field] = value;
    setStudentDetails(updatedDetails);
  };
  
  // Generate reports using OpenAI API
  const generateReports = async () => {
    setIsGenerating(true);

    try {
      const response = await fetch("https://aiautomarker.com/api/generate-reports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          students: studentDetails,
          classInfo,
        }),
      });

      const data = await response.json();

      if (data?.reports) {
        // Add level information to each report
        const reportsWithLevel = data.reports.map(report => {
          const studentDetail = studentDetails.find(student => student.name === report.name);
          return {
            ...report,
            level: studentDetail ? studentDetail.level : null
          };
        });
        setReports(reportsWithLevel);
        setCurrentStep(4);
      } else {
        alert("Failed to generate reports.");
      }
    } catch (error) {
      console.error("Error calling /api/generate-reports:", error);
      alert("An error occurred while generating the reports.");
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Copy report to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Report copied to clipboard!');
  };
  
  // Download all reports as CSV
  const downloadAsCSV = () => {
    // Create CSV content
    let csvContent = "Student Name,Level,Report\n";
    reports.forEach(report => {
      // Escape quotes in the report text and wrap in quotes
      const escapedReport = report.report.replace(/"/g, '""');
      csvContent += `"${report.name}","${report.level}","${escapedReport}"\n`;
    });
    
    // Create a blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${classInfo.className}_${classInfo.subject}_Reports.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Print reports as PDF
  const printReports = () => {
    // Create a new window with just the reports content
    const printWindow = window.open('', '_blank');
    
    // Create the HTML content for the print window
    let htmlContent = `
      <html>
        <head>
          <title>${classInfo.className} ${classInfo.subject} Reports</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .report { margin-bottom: 30px; page-break-after: always; }
            h2 { color: #2c3e50; }
            p { line-height: 1.6; }
            .header { margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${classInfo.className} ${classInfo.subject} Reports - ${classInfo.term}</h1>
          </div>
    `;
    
    // Add each report
    reports.forEach(report => {
      htmlContent += `
        <div class="report">
          <h2>${report.name} - Level ${report.level}</h2>
          <p>${report.report}</p>
        </div>
      `;
    });
    
    // Close the HTML
    htmlContent += `
        </body>
      </html>
    `;
    
    // Write to the new window and print
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Wait for content to load before printing
    printWindow.onload = function() {
      printWindow.print();
    };
  };
  
  // Reset the form to start a new batch
  const startNewBatch = () => {
    setCurrentStep(1);
    setStudentNamesInput('');
    setStudentNames([]);
    setNameError('');
    setClassInfo({
      className: '',
      subject: '',
      term: '',
      unitsCovered: ''
    });
    setStudentDetails([]);
    setReports([]);
  };
  
  // Render progress indicator
  const renderProgressIndicator = () => {
    return (
      <div className="progress-indicator">
        {[1, 2, 3, 4].map(step => (
          <div 
            key={step} 
            className={`progress-step ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
          >
            <div className="step-circle">{step}</div>
            <div className="step-label">
              {step === 1 ? 'Student Input' : 
               step === 2 ? 'Class Details' : 
               step === 3 ? 'Individual Details' : 
               'Reports'}
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  // Render Step 1: Student Input
  const renderStudentInput = () => {
    return (
      <div className="step-container">
        <h2>Step 1: Student Input</h2>
        
        <div className="form-group">
          <label htmlFor="studentNames">Enter student names, separated by commas (up to 30)</label>
          <textarea
            id="studentNames"
            placeholder="John, Sarah, Abdulla"
            value={studentNamesInput}
            onChange={handleStudentNamesChange}
          />
          {nameError && <div className="error-message">{nameError}</div>}
        </div>
        
        <button className="next-button" onClick={processStudentNames}>
          Next
        </button>
        
        {studentNames.length > 0 && (
          <div className="student-name-edit">
            <h3>Edit Student Names</h3>
            {studentNames.map((name, index) => (
              <div key={index} className="form-group">
                <label htmlFor={`student-${index}`}>Student {index + 1}</label>
                <input
                  id={`student-${index}`}
                  type="text"
                  value={name}
                  onChange={(e) => updateStudentName(index, e.target.value)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };
  
  // Render Step 2: Shared Class Details
  const renderClassDetails = () => {
    return (
      <div className="step-container">
        <h2>Step 2: Shared Class Details</h2>
        
        <div className="form-group">
          <label htmlFor="className">Class</label>
          <input
            type="text"
            id="className"
            name="className"
            placeholder="e.g., 9A"
            value={classInfo.className}
            onChange={handleClassInfoChange}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="subject">Subject</label>
          <input
            type="text"
            id="subject"
            name="subject"
            placeholder="e.g., Computing"
            value={classInfo.subject}
            onChange={handleClassInfoChange}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="term">Term</label>
          <input
            type="text"
            id="term"
            name="term"
            placeholder="e.g., Term 3"
            value={classInfo.term}
            onChange={handleClassInfoChange}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="unitsCovered">Units Covered</label>
          <textarea
            id="unitsCovered"
            name="unitsCovered"
            placeholder="e.g., Unit 1: Data Representation, Unit 2: Python"
            value={classInfo.unitsCovered}
            onChange={handleClassInfoChange}
          />
        </div>
        
        <div className="button-group">
          <button className="back-button" onClick={() => setCurrentStep(1)}>
            Back
          </button>
          <button className="next-button" onClick={proceedToStudentDetails}>
            Next
          </button>
        </div>
      </div>
    );
  };
  
  // Render Step 3: Individual Student Details
  const renderStudentDetails = () => {
    return (
      <div className="step-container">
        <h2>Step 3: Individual Student Details</h2>
        
        <div className="student-details-container">
          {studentDetails.map((student, index) => (
            <div key={index} className="student-card">
              <h3>{student.name}</h3>
              
              <div className="form-group">
                <label htmlFor={`level-${index}`}>Level (1-9)</label>
                <select
                  id={`level-${index}`}
                  value={student.level}
                  onChange={(e) => handleStudentDetailChange(index, 'level', parseInt(e.target.value))}
                >
                  <option value={1}>1 - Emerging</option>
                  <option value={2}>2 - Emerging</option>
                  <option value={3}>3 - Developing</option>
                  <option value={4}>4 - Developing</option>
                  <option value={5}>5 - Secure</option>
                  <option value={6}>6 - Proficient</option>
                  <option value={7}>7 - Proficient</option>
                  <option value={8}>8 - Accomplished</option>
                  <option value={9}>9 - Accomplished</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor={`positives-${index}`}>Positives to Mention</label>
                <textarea
                  id={`positives-${index}`}
                  value={student.positives}
                  onChange={(e) => handleStudentDetailChange(index, 'positives', e.target.value)}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor={`improve-${index}`}>Areas to Improve</label>
                <textarea
                  id={`improve-${index}`}
                  value={student.areasToImprove}
                  onChange={(e) => handleStudentDetailChange(index, 'areasToImprove', e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
        
        <div className="button-group">
          <button className="back-button" onClick={() => setCurrentStep(2)}>
            Back
          </button>
          <button 
            className="generate-button" 
            onClick={generateReports}
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Generate All Reports'}
          </button>
        </div>
      </div>
    );
  };
  
  // Render Step 4: Generated Reports
  const renderReports = () => {
    return (
      <div className="step-container">
        <h2>Generated Reports</h2>
        
        <div className="reports-container">
          {reports.map((report, index) => (
            <div key={index} className="report-card">
              <h3>{report.name} - Level {report.level}</h3>
              <p className="report-text">{report.report}</p>
              <button 
                className="copy-button"
                onClick={() => copyToClipboard(report.report)}
              >
                Copy
              </button>
            </div>
          ))}
        </div>
        
        <div className="download-options">
          <button 
            className="download-button csv-button"
            onClick={downloadAsCSV}
          >
            Download as CSV
          </button>
          <button 
            className="download-button pdf-button"
            onClick={printReports}
          >
            Print as PDF
          </button>
        </div>
        
        <button className="back-button" onClick={startNewBatch}>
          Start New Batch
        </button>
      </div>
    );
  };
  
  // Navigate back to dashboard
  const goToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div className="auto-report-container">
      <div className="header-container">
        <h1>AI AutoReport</h1>
        <button className="dashboard-button" onClick={goToDashboard}>
          Back to Dashboard
        </button>
      </div>
      {renderProgressIndicator()}
      
      {currentStep === 1 && renderStudentInput()}
      {currentStep === 2 && renderClassDetails()}
      {currentStep === 3 && renderStudentDetails()}
      {currentStep === 4 && renderReports()}
    </div>
  );
};

export default AutoReport;
